import { Transition } from '@headlessui/react';
import chevronDown from '@iconify/icons-heroicons-outline/chevron-down';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import MemoizedIcon from '@components/MemoizedIcon';
import { Config, useSidebarContext } from '@src/context/SidebarContext';

interface MenuListProps {
  menuConfigs: Config[];
  depth?: number;
  closeSidebar?: () => void;
}

const RecursiveMenu = ({
  menuConfigs,
  depth = 0,
  closeSidebar,
}: MenuListProps) => {
  const { push } = useHistory();
  const { t } = useTranslation();

  const { selectedMenu, activeIndexMap, handleSetActiveIndexMap } =
    useSidebarContext();

  const extraLeftPadding = depth ? 40 : 20;
  const dynamicLeftPadding = extraLeftPadding + depth * 20;

  return (
    <div>
      {menuConfigs.map((parent, index) => {
        const hasChild = parent.children?.length;

        const hasAvailableChild =
          hasChild && parent.children?.find(child => child.isAvailable);

        const shouldHide = hasChild
          ? !parent.isAvailable || !hasAvailableChild
          : !parent.isAvailable;

        if (shouldHide) {
          return null;
        }
        const isSelected = selectedMenu === parent.label;
        const isCollapsed = !activeIndexMap[depth]?.includes(index);

        const handleClick = () => {
          if (hasChild) {
            handleSetActiveIndexMap(depth, index, isCollapsed);
          } else {
            parent?.path && push(parent.path);
            closeSidebar?.();
          }
        };

        return (
          <div key={parent.label}>
            <div
              style={{ paddingLeft: dynamicLeftPadding }}
              className={twMerge(
                'flex hover:bg-primary-hover pr-5 py-4 cursor-pointer',
                isSelected
                  ? 'bg-cardSelectedColor'
                  : 'bg-white hover:bg-neutral-30',
              )}
              onClick={handleClick}
              data-testid={`recursive-menu-${parent.label}`}
              key={parent.label}
            >
              {parent.icon && (
                <MemoizedIcon icon={parent.icon} className="h-6 w-6 mr-4" />
              )}
              <div
                className={twMerge(
                  parent?.labelClass,
                  isSelected && 'font-semibold',
                )}
              >
                {t(parent.label)}
              </div>

              {parent.children && (
                <MemoizedIcon
                  icon={chevronDown}
                  data-testid={`chevron-${parent.label}`}
                  className={twMerge(
                    'h-6 w-6 ml-auto transition-transform',
                    !isCollapsed ? 'rotate-180' : 'rotate-0',
                  )}
                />
              )}
            </div>
            {parent.children && (
              <Transition
                show={!isCollapsed}
                enter="ease-in-out duration-500"
                enterFrom="opacity-0 h-0"
                enterTo="opacity-100 h-fit"
                leave="ease-in-out duration-200"
                leaveFrom="opacity-100 h-fit"
                leaveTo="opacity-0 h-0"
                data-testid={`collapsible-child-${parent.label}`}
              >
                <RecursiveMenu
                  menuConfigs={parent.children}
                  depth={depth + 1}
                  closeSidebar={closeSidebar}
                />
              </Transition>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default RecursiveMenu;
